import { ChangeEvent, Dispatch, FC, FormEvent, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { Dialog, Loader, SearchableSelect, useDialog } from 'components';
import { BookingDetails, RequiredBooking, sslScacs } from 'types';

import useTimeZone from 'hooks/use-timezone';
import { useGateNewBooking } from './new-booking';
import './new-booking.scss';

interface EditBookingDetailsProps {
  bookingDetails: BookingDetails;
  setBookingDetails: Dispatch<SetStateAction<BookingDetails>>;
}

const EditBookingDetails: FC<EditBookingDetailsProps> = ({ bookingDetails, setBookingDetails }) => {
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      setBookingDetails(prevState => ({ ...prevState, count: Math.max(1, Number(e.target.value)) })),
    [setBookingDetails]
  );
  const handleStep = useCallback(
    (step: -1 | 1) =>
      setBookingDetails(prevState => ({ ...prevState, count: Math.max(1, (prevState.count ?? 0) + step) })),
    [setBookingDetails]
  );
  const baseId = bookingDetails.iso?.join('-');
  return (
    <>
      <div className="d-flex justify-content-between">
        <label htmlFor={`${baseId}-size-type`}>QUANTITY</label>
        <label htmlFor={`${baseId}-carb`}>CARB</label>
      </div>
      <div className="d-flex justify-content-between">
        <div className="flex-grow-2 p-relative quantity-stepper g-3">
          <button type="button" onClick={() => handleStep(-1)}>
            <span className="material-icons">remove</span>
          </button>
          <input
            id={`${baseId}-size-type`}
            inputMode="numeric"
            value={bookingDetails.count ?? 0}
            onChange={handleChange}
            onFocus={e => e.target.setSelectionRange(0, e.target.value.length)}
          />
          <button type="button" onClick={() => handleStep(1)}>
            <span className="material-icons">add</span>
          </button>
          <label className={`p-absolute l-100 text-info-main `}>&times;&nbsp;{bookingDetails.iso}</label>
        </div>
        <input
          id={`${baseId}-carb`}
          type="checkbox"
          className="flex-grow-1"
          defaultChecked={!!bookingDetails.carb}
          disabled
        />
      </div>
    </>
  );
};

export const GateEditBooking: FC = () => {
  const { closeDialog, openDialog, ref } = useDialog();
  const { editedBooking, setBookings, setEditedBooking, postProcessFn, setPostProcessFn } = useGateNewBooking();
  const { dateInputToUtc, utcToZonedDate } = useTimeZone();

  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const setBookingDetails = useMemo(() => {
    return (index: number) => {
      return (action: SetStateAction<BookingDetails>) => {
        setEditedBooking(prevState => {
          if (!prevState?.bookingDetails) return prevState;
          const bookingDetails = [...prevState.bookingDetails];
          if (index in bookingDetails) {
            bookingDetails[index] = typeof action === 'function' ? action(bookingDetails[index]) : action;
          }
          return { ...prevState, bookingDetails };
        });
      };
    };
  }, [setEditedBooking]);

  useEffect(() => {
    if (editedBooking?.id && !ref.current?.open) {
      openDialog();
    } else if (!editedBooking && ref.current?.open) {
      closeDialog();
    }
  }, [editedBooking, ref, openDialog, closeDialog]);

  const handleReset = useCallback(() => {
    setPostProcessFn(() => {});
    setEditedBooking(undefined);
    setShowDetails(false);
    setLoading(false);
    closeDialog();
  }, [closeDialog, setLoading, setEditedBooking, setPostProcessFn, setShowDetails]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);
      setBookings(b => [...b, editedBooking as RequiredBooking]);
      postProcessFn();
      handleReset();
    },
    [editedBooking, handleReset, postProcessFn, setBookings]
  );

  return (
    <Dialog closeDialog={handleReset} ref={ref} width="400px">
      <Loader loading={loading || !postProcessFn}>
        <form id="gate-new-booking" className="d-flex flex-column" onReset={handleReset} onSubmit={handleSubmit}>
          <h3 className="col-span-4 text-primary-dark">EDIT BOOKING QUANTITY</h3>
          <label htmlFor="booking-number">BOOKING</label>
          <input id="booking-number" type="text" disabled value={editedBooking?.bookingNumber ?? ''} />
          {showDetails && (
            <>
              <label htmlFor="customer">CUSTOMER</label>
              <input id="customer" type="text" disabled value={editedBooking?.customer?.name ?? ''} />
              <label htmlFor="location">LOCATION</label>
              <input id="location" type="text" disabled value={editedBooking?.location?.name ?? ''} />
              <label htmlFor="start-date">START DATE</label>
              <input id="start-date" type="date" disabled value={utcToZonedDate(editedBooking?.startDate)} />
              <label htmlFor="ssl-scac">SSL SCAC</label>
              <SearchableSelect
                disabled
                options={sslScacs.map(scac => ({ label: scac, value: scac }))}
                value={editedBooking?.sslScac ?? undefined}
                onValueSelected={scac => setEditedBooking(b => b && { ...b, sslScac: scac })}
              />
            </>
          )}
          <button type="button" className="btn-close col-span-4 py-0" onClick={() => setShowDetails(p => !p)}>
            <span className="material-icons icon-large py-2">more_{showDetails ? 'horiz' : 'vert'}</span>
          </button>
          <label htmlFor="expiration-date">EXPIRATION</label>
          <input
            type="date"
            id="expiration-date"
            disabled
            value={utcToZonedDate(editedBooking?.expirationDate)}
            onChange={e => setEditedBooking(b => b && { ...b, expirationDate: dateInputToUtc(e.target.value) })}
          />
          {editedBooking?.bookingDetails?.map((bd, i) => (
            <EditBookingDetails key={bd.iso?.join('-')} bookingDetails={bd} setBookingDetails={setBookingDetails(i)} />
          ))}
          <label htmlFor="notes">NOTES</label>
          <textarea
            id="notes"
            value={editedBooking?.notes ?? ''}
            onChange={e => setEditedBooking(b => b && { ...b, notes: e.target.value })}
          />
          <div className="d-flex justify-content-between mt-4">
            <div className="d-flex g-4">
              <button type="reset" className="btn-outline col-start-3">
                cancel
              </button>
              <button type="submit" className="btn-primary col-start-4">
                submit
              </button>
            </div>
          </div>
        </form>
      </Loader>
    </Dialog>
  );
};

export default GateEditBooking;
